import { useCallback, useEffect, useRef, useState } from "react";

import { UnifiedCarouselImage } from "@/src/types/carousel";

interface UseUnifiedCarouselControllerProps {
  images: UnifiedCarouselImage[];
  initialIndex?: number;
  loop?: boolean;
  autoplay?: boolean;
  autoplayIntervalMs?: number;
  pauseOnHover?: boolean;
  onIndexChange?: (index: number) => void;
}

interface UseUnifiedCarouselControllerReturn {
  currentIndex: number;
  isPaused: boolean;
  goNext: () => void;
  goPrevious: () => void;
  goToIndex: (index: number) => void;
  pauseAutoplay: () => void;
  resumeAutoplay: () => void;
  openLightbox: () => void;
  closeLightbox: () => void;
  isLightboxOpen: boolean;
  lightboxIndex: number;
  setLightboxIndex: (index: number) => void;
}

export const useUnifiedCarouselController = (
  props: UseUnifiedCarouselControllerProps
): UseUnifiedCarouselControllerReturn => {
  const {
    images,
    initialIndex = 0,
    loop = true,
    autoplay = false,
    autoplayIntervalMs = 5000,
    onIndexChange
  } = props;

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPaused, setIsPaused] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(initialIndex);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const totalImages = images.length;

  const goNext = useCallback(() => {
    if (totalImages === 0) return;

    setCurrentIndex((prev) => {
      return loop
        ? (prev + 1) % totalImages
        : Math.min(prev + 1, totalImages - 1);
    });
  }, [totalImages, loop]);

  const goPrevious = useCallback(() => {
    if (totalImages === 0) return;

    setCurrentIndex((prev) => {
      return loop
        ? (prev - 1 + totalImages) % totalImages
        : Math.max(prev - 1, 0);
    });
  }, [totalImages, loop]);

  const goToIndex = useCallback(
    (index: number) => {
      if (totalImages === 0 || index < 0 || index >= totalImages) return;

      setCurrentIndex(index);
    },
    [totalImages]
  );

  // Call onIndexChange callback when currentIndex changes
  useEffect(() => {
    onIndexChange?.(currentIndex);
  }, [currentIndex, onIndexChange]);

  const pauseAutoplay = useCallback(() => {
    setIsPaused(true);
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  }, []);

  const resumeAutoplay = useCallback(() => {
    setIsPaused(false);
  }, []);

  const openLightbox = useCallback(() => {
    setLightboxIndex(currentIndex);
    setIsLightboxOpen(true);
  }, [currentIndex]);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
  }, []);

  // Sync lightbox index with main carousel when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      setLightboxIndex(currentIndex);
    }
  }, [currentIndex, isLightboxOpen]);

  // Autoplay logic
  useEffect(() => {
    if (!autoplay || totalImages <= 1 || isPaused) {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
      return;
    }

    autoplayTimerRef.current = setInterval(() => {
      goNext();
    }, autoplayIntervalMs);

    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    };
  }, [autoplay, totalImages, isPaused, autoplayIntervalMs, goNext]);

  // Pause/resume functions are exposed for the view component to use
  // The view component will attach mouse enter/leave handlers

  // Update current index when initialIndex changes externally
  useEffect(() => {
    if (initialIndex >= 0 && initialIndex < totalImages) {
      setCurrentIndex(initialIndex);
    }
  }, [initialIndex, totalImages]);

  return {
    currentIndex,
    isPaused,
    goNext,
    goPrevious,
    goToIndex,
    pauseAutoplay,
    resumeAutoplay,
    openLightbox,
    closeLightbox,
    isLightboxOpen,
    lightboxIndex,
    setLightboxIndex
  };
};
